// Import react.
import * as React from 'react';
// Import props.
import {
	InputCheckboxProps,
} from '@app/props';
// Import api models.
import {
	AcknowledgementText,
} from '@app/models/api';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';

interface UseInputAcknowledgementParameters {
	id: string;
	disabled: boolean;
	required: boolean;
	acknowledgements: AcknowledgementText[];
	showValidation?: boolean;
}

interface UseInputAcknowledgementResults {
	acknowledgements: AcknowledgementText[];
	focusToAcknowledgementInput: (acknowledgementId: number) => void;
	checkValidationWithFocus: () => boolean;
	changeShowValidationFlag: (showValidation: boolean) => void;
	doAcknowledgementsHaveChanges: () => boolean;
	applyAcknowledgementProps: (acknowledgementId: number) => InputCheckboxProps | undefined
}

const useInputAcknowledgement = (params: UseInputAcknowledgementParameters): UseInputAcknowledgementResults => {

	// #region exported methods

	/**
	 * Focuses to acknowledgement input. 
	 **/
	const focusToAcknowledgementInput = (acknowledgementId: number): void => {
		if (queuedAcknowledgementIdToFocus === undefined) {
			setQueuedAcknowledgementIdToFocus(acknowledgementId);
		}
	};

	/**
	 * Method to check form validation with focus. 
	 **/
	const checkValidationWithFocus = (): boolean => {
		let areAcknowledgementsValid: boolean = true;
		if (!params.disabled) {
			// Validate all the acknowledgements.
			areAcknowledgementsValid = acknowledgements.every((acknowledgement: AcknowledgementText) => {
				let isValid: boolean = true;
				let acknowledgementId: number | null = acknowledgement.textId;
				// Validate the acknowledgement.
				isValid = validateAcknowledgement(acknowledgement, params.required).isValid;
				// Scroll and focus to input if invalid.
				if (!isValid
					&& acknowledgementId !== null) {
					// Focus on input.
					focusToAcknowledgementInput(acknowledgementId);

				}
				return isValid;
			});
		}

		return areAcknowledgementsValid;
	}

	/**
	 * Sets the show validation flag in the state.
	 * @param showValidation
	 */
	const changeShowValidationFlag = (showValidation: boolean): void => {
		setShowValidation(showValidation);
	}

	/**
	 * Checks to see if the acknowledgements have changes. 
	 **/
	const doAcknowledgementsHaveChanges = (): boolean => {
		return hasChanges;
	}

	/**
	 * Applies the checkbox input props for the acknowledgement.
	 * @param acknowledgementId
	 */
	const applyAcknowledgementProps = (acknowledgementId: number): InputCheckboxProps | undefined => {
		let props: InputCheckboxProps | undefined = undefined;
		// Fetch the matching acknowledgement.
		let acknowledgement: AcknowledgementText | undefined = getAcknowledgement(acknowledgementId);
		if (acknowledgement !== undefined) {
			// Validate input, if needed.
			let validationResult: ValidatorResponse = !params.disabled
				? validateAcknowledgement(acknowledgement, params.required)
				: new ValidatorResponse(true, String.empty());
			// Generate the element id for the acknowledgement.
			let acknowledgementElementId: string = generateIdForAcknowledgementElement(acknowledgementId);
			// Assign the prop values.
			props = {
				id: acknowledgementElementId,
				label: 'I acknowledge that I have read and understand the above.',
				hasErrors: showValidation ? !validationResult.isValid : false,
				helperText: showValidation ? validationResult.message : undefined,
				isOptional: !params.required,
				disabled: params.disabled,
				inputRef: (ref: React.RefObject<any>) => registerInputRef(acknowledgementId, ref),
				onChange: (isChecked: boolean) => setAcknowledgement(acknowledgementId, isChecked),
				isChecked: acknowledgement.isAcknowledged ?? false,
			};
		}

		return props;
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Validates the acknowledgement.
	 * @param acknowledgement
	 * @param required
	 */
	const validateAcknowledgement = (acknowledgement: AcknowledgementText, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required
			&& !acknowledgement.isAcknowledged) {
			// Make sure the text is acknowledged.
			response = new ValidatorResponse(false, 'Please acknowledge the statement above inorder to continue.');
		}

		return response;
	}

	/**
	 * Finds a matching acknowledgement record..
	 * @param acknowledgementId
	 */
	const getAcknowledgement = (acknowledgementId: number): AcknowledgementText | undefined => {
		return params.acknowledgements.find((acknowledgement: AcknowledgementText) => acknowledgement.textId === acknowledgementId);
	}

	/**
	 * Sets the check status of the acknowledgement input.
	 * @param acknowledgementId
	 * @param isChecked
	 */
	const setAcknowledgement = (acknowledgementId: number, isChecked: boolean): void => {
		// Updated the acknowledgements
		setAcknowledgements((currentAcknowledgements: AcknowledgementText[]) => {
			// Create copy of the current acknowledgements.
			let copiedAcknowledgements: AcknowledgementText[] = [...currentAcknowledgements];
			// For each selected peril deductible.
			return copiedAcknowledgements.map((copiedAcknowledgement: AcknowledgementText) => {
				if (copiedAcknowledgement.textId === acknowledgementId) {
					copiedAcknowledgement.isAcknowledged = isChecked;
				}
				return copiedAcknowledgement;
			});
		});
		// Any updates should flag the inputs of having changes.
		setHasChanges(true);
	}

	/**
	 * Registers the input ref.
	 * @param acknowledgementId
	 * @param ref
	 */
	const registerInputRef = (acknowledgementId: number, ref: React.RefObject<any>): void => {
		let inputRef: React.RefObject<any> | undefined = inputRefs.get(acknowledgementId);
		if (inputRef === undefined
			|| inputRef.current === null) {
			setInputRefs((currentInputRefs: Map<number, React.RefObject<any>>) => {
				let copiedInputRefs: Map<number, React.RefObject<any>> = new Map<number, React.RefObject<any>>(currentInputRefs);
				copiedInputRefs.set(acknowledgementId, ref);
				return copiedInputRefs;
			});
		}
	}

	// #region id generators

	/**
	 * Generates id. 
	 **/
	const generateId = (): string => {
		return params.id;
	}

	/**
	 * Generates id for acknowledgement.
	 * @param acknowledgementId
	 */
	const generateIdForAcknowledgementElement = (acknowledgementId: number): string => {
		return `${generateId()}-acknowledgement-${acknowledgementId}`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	const [acknowledgements, setAcknowledgements] = React.useState<AcknowledgementText[]>(params.acknowledgements);
	const [showValidation, setShowValidation] = React.useState<boolean>(params.showValidation ?? false);
	const [hasChanges, setHasChanges] = React.useState<boolean>(false);
	const [inputRefs, setInputRefs] = React.useState<Map<number, React.RefObject<any>>>(new Map<number, React.RefObject<any>>());
	const [queuedAcknowledgementIdToFocus, setQueuedAcknowledgementIdToFocus] = React.useState<number | undefined>(undefined);

	// #endregion variables


	// #region life-cycle methods.

	// Called when input ref or when the focus-to flag is updated.
	React.useEffect(() => {
		// If there is a queued input focus-to flag and the input reference is ready, then proceed to focus on the input.
		if (queuedAcknowledgementIdToFocus
			&& inputRefs.has(queuedAcknowledgementIdToFocus)) {
			// Find the matching input ref.
			let inputRef: React.RefObject<any> | undefined = inputRefs.get(queuedAcknowledgementIdToFocus);
			if (inputRef !== undefined
				&& inputRef.current !== null) {
				// If input is already focused, then do not attempt to do it again.
				if (document.activeElement !== inputRef.current) {
					inputRef.current.focus();
				}
				// Reset the queued input to focus.
				setQueuedAcknowledgementIdToFocus(undefined);
			}
		}
	}, [queuedAcknowledgementIdToFocus, inputRefs]);

	// #endregion life-cycle methods.


	return {
		acknowledgements: acknowledgements,
		focusToAcknowledgementInput: focusToAcknowledgementInput,
		checkValidationWithFocus: checkValidationWithFocus,
		changeShowValidationFlag: changeShowValidationFlag,
		doAcknowledgementsHaveChanges: doAcknowledgementsHaveChanges,
		applyAcknowledgementProps: applyAcknowledgementProps,
	};
}

// Export parameters.
export {
	UseInputAcknowledgementParameters,
}

// Export results.
export {
	UseInputAcknowledgementResults,
}

export default useInputAcknowledgement;